import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import {
  ProjectSiteDiaryEntryType,
  ProjectSiteDiaryRecord
} from '@modules/projects/shared/interfaces/project-site-diary.interface';
import { publicFile } from '@shared/helpers/public-file.helper';
import { Config } from '@shared/configs/config';

export interface ProjectSiteDiaryEntryTypeView {
  type: string;
  label: string;
  icon: string;
  foregroundColor: string;
  backgroundColor: string;
  order: number;
}

export interface ProjectSiteDiaryEntryTypeConfig {
  translationKey: string;
  icon: string;
  foregroundColor: string;
  backgroundColor: string;
  order: number;
}

export interface ProjectSiteDiaryRowView {
  id: number;
  created: string;
  createdLabel: string;
  creatorContactId: number | null;
  creatorAvatarUrl: string | null;
  creatorName: string;
  projectId: number | null;
  projectName: string;
  entryTypes: ProjectSiteDiaryEntryTypeView[];
  sourceRecord: ProjectSiteDiaryRecord;
}

type CreatorNameFallback = 'email' | 'notificationName' | 'emailOrNotificationName' | null;

export function mapProjectSiteDiaryRecordToRow(
  record: ProjectSiteDiaryRecord,
  translateService: TranslateService,
  creatorNameFallback: CreatorNameFallback = null
): ProjectSiteDiaryRowView {
  return {
    id: record.id,
    created: record.created,
    createdLabel: formatSiteDiaryCreated(record.created, translateService.currentLang),
    creatorContactId: record.creator?.contactId ?? null,
    creatorAvatarUrl: getCreatorAvatarUrl(record),
    creatorName: getCreatorName(record, creatorNameFallback),
    projectId: record.project?.id ?? null,
    projectName: getProjectName(record),
    entryTypes: getEntryTypes(record.entryTypes, translateService),
    sourceRecord: record
  };
}

function getProjectName(record: ProjectSiteDiaryRecord): string {
  const project = record.project;
  if (!project) return '-';

  return (
    [project.mainContactFullName, project.name, project.fullAddress || project.address]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' | ') || '-'
  );
}

function getCreatorName(record: ProjectSiteDiaryRecord, fallback: CreatorNameFallback): string {
  const firstName = String(record.creator?.userPerson?.firstName || '').trim();
  const lastName = String(record.creator?.userPerson?.lastName || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const person = record.creator?.userPerson;
  const fallbackValue =
    fallback === 'emailOrNotificationName'
      ? String(person?.email || person?.notificationName || '').trim()
      : String(person?.[fallback || ''] || '').trim();

  if ((!firstName || !lastName) && fallbackValue) return fallbackValue;
  return fullName || '-';
}

function getCreatorAvatarUrl(record: ProjectSiteDiaryRecord): string | null {
  const photo = String(record.creator?.userPerson?.photo || '').trim();
  return photo ? publicFile(photo, 100) : null;
}

function getEntryTypes(
  entryTypes: Array<ProjectSiteDiaryEntryType | string> | null | undefined,
  translateService: TranslateService
): ProjectSiteDiaryEntryTypeView[] {
  const uniqueTypes = new Map<string, ProjectSiteDiaryEntryTypeView>();

  for (const entryType of entryTypes || []) {
    const normalizedType = normalizeEntryType(entryType);
    if (!normalizedType) continue;
    if (!uniqueTypes.has(normalizedType)) {
      uniqueTypes.set(normalizedType, getEntryTypeView(normalizedType, translateService));
    }
  }

  return [...uniqueTypes.values()].sort((left, right) => left.order - right.order);
}

function normalizeEntryType(entryType: ProjectSiteDiaryEntryType | string): string {
  if (typeof entryType === 'string') return entryType.trim().toLowerCase();

  const type = String(entryType?.type || '')
    .trim()
    .toLowerCase();
  if (type) return type;

  return '';
}

function getEntryTypeView(type: string, translateService: TranslateService): ProjectSiteDiaryEntryTypeView {
  const config = getEntryTypeConfig(type);

  return {
    type,
    label: translateService.instant(`ProjectsDashboard.siteDiaryPage.entryTypes.${config.translationKey}`),
    icon: config.icon,
    foregroundColor: config.foregroundColor,
    backgroundColor: config.backgroundColor,
    order: config.order
  };
}

export function getEntryTypeConfig(type: string): ProjectSiteDiaryEntryTypeConfig {
  switch (type) {
    case 'site_update':
      return {
        translationKey: 'siteUpdate',
        icon: 'sticky_note_2',
        foregroundColor: '#437EF7',
        backgroundColor: 'rgba(67,126,247,0.14)',
        order: 0
      };
    case 'task':
      return {
        translationKey: 'task',
        icon: 'done',
        foregroundColor: '#536277',
        backgroundColor: 'rgba(83,98,119,0.14)',
        order: 3
      };
    case 'change':
      return {
        translationKey: 'change',
        icon: 'sync_alt',
        foregroundColor: '#F08A24',
        backgroundColor: 'rgba(240,138,36,0.14)',
        order: 4
      };
    case 'order':
      return {
        translationKey: 'order',
        icon: 'shopping_cart',
        foregroundColor: '#17A765',
        backgroundColor: 'rgba(23,167,101,0.14)',
        order: 5
      };
    case 'correction':
      return {
        translationKey: 'correction',
        icon: 'construction',
        foregroundColor: '#E53935',
        backgroundColor: 'rgba(229,57,53,0.14)',
        order: 6
      };
    default:
      return {
        translationKey: 'siteUpdate',
        icon: 'sticky_note_2',
        foregroundColor: '#437EF7',
        backgroundColor: 'rgba(67,126,247,0.14)',
        order: 0
      };
  }
}

function formatSiteDiaryCreated(value: string, language?: string): string {
  if (!value) return '-';

  try {
    const parsedDate = moment(value, [Config.DATE_SERVER, moment.ISO_8601], true);

    if (!parsedDate.isValid()) return value;

    return parsedDate.locale(normalizeLocale(language)).format(Config.DATE_FORMAT_DOTS);
  } catch {
    return value;
  }
}

function normalizeLocale(language?: string): string {
  switch (language) {
    case 'de':
      return 'de-DE';
    case 'pl':
      return 'pl-PL';
    default:
      return 'en-US';
  }
}
